import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Wallet, Banknote, Landmark, BedDouble, Clock,
  ChevronRight, BarChart3, AlertTriangle, ArrowUpRight, ArrowDownRight,
} from 'lucide-react';
import {
  usePLCAvailableMonths,
  usePLCCombinedSummary,
} from '../../hooks/useProjectLabourCostData';

// The full operational PLC report lives under admin reports; the dashboard
// is the at-a-glance awareness layer that deep-links into it.
const FULL_REPORT = '/admin/reports/project-labour-cost';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const MONTH_NAMES_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const fmtMoney = (value) =>
  `$${Math.round(Number(value) || 0).toLocaleString('en-US')}`;

const sharePct = (part, whole) => {
  const w = Number(whole) || 0;
  if (w <= 0) return 0;
  return Math.round(((Number(part) || 0) / w) * 100);
};

// Roll the combined report rows up to one figure per project so HR sees
// "where did the money go" without the full per-row report.
const buildTopProjects = (rows, limit = 5) => {
  const byProject = new Map();
  for (const row of rows || []) {
    const key = row.project_code || row.project_name || 'Unallocated';
    const existing = byProject.get(key) || {
      key,
      name: row.project_name || row.project_code || 'Unallocated',
      code: row.project_code || null,
      total: 0,
    };
    existing.total += Number(row.total_cost) || 0;
    byProject.set(key, existing);
  }
  return Array.from(byProject.values())
    .filter((p) => p.total > 0)
    .sort((a, b) => b.total - a.total)
    .slice(0, limit);
};

const ProjectLabourCostSection = () => {
  const monthsQuery = usePLCAvailableMonths();
  const months = monthsQuery.data || [];

  // Anchor on the latest period that has a closed payroll run (salary is the
  // core of labour cost); fall back to the most recent month with any data.
  const anchor = useMemo(() => {
    if (!months.length) return null;
    return months.find((m) => m.has_payroll) || months[0];
  }, [months]);

  const summaryQuery = usePLCCombinedSummary(anchor?.year, anchor?.month);
  const report = summaryQuery.data;

  // Previous period for the month-over-month delta: the next-older month that
  // also has a closed payroll run (skips data-less gaps for a fair comparison).
  const prior = useMemo(() => {
    if (!anchor || !months.length) return null;
    const idx = months.findIndex(
      (m) => m.year === anchor.year && m.month === anchor.month
    );
    if (idx < 0) return null;
    return months.slice(idx + 1).find((m) => m.has_payroll) || null;
  }, [anchor, months]);

  // Loads in the background; the delta simply appears once it resolves and is
  // hidden entirely if there's no comparable prior month.
  const priorQuery = usePLCCombinedSummary(prior?.year, prior?.month);

  const topProjects = useMemo(
    () => buildTopProjects(report?.rows),
    [report]
  );

  // Non-HR users get a 403 on these endpoints; render nothing rather than an
  // error box, matching the Levy Optimization section's behaviour.
  if (monthsQuery.isError || summaryQuery.isError) return null;

  const isLoading =
    monthsQuery.isLoading || (!!anchor && summaryQuery.isLoading);

  const gt = report?.grand_totals || {};
  const totalCost = gt.total_cost ?? 0;
  const salaryCost = parseFloat(report?.source_totals?.salary ?? 0) || 0;
  const levyCost = parseFloat(report?.source_totals?.levy ?? 0) || 0;
  const dormCost = parseFloat(report?.source_totals?.dorm ?? 0) || 0;

  // Overtime = cost attributed to weekday-OT + rest-day(Sunday)-OT hours. The
  // most controllable cost lever; a slice of total, not an extra pillar.
  const otCost = (Number(gt.weekday_ot_cost) || 0) + (Number(gt.sunday_ot_cost) || 0);

  // Month-over-month change in total labour cost vs the prior data month.
  const priorTotal = priorQuery.data?.grand_totals?.total_cost ?? null;
  const momPct =
    prior && priorTotal && priorTotal > 0
      ? ((totalCost - priorTotal) / priorTotal) * 100
      : null;
  const priorLabel = prior ? MONTH_NAMES_SHORT[prior.month - 1] : '';

  const topMax = topProjects.length ? topProjects[0].total : 0;

  const periodLabel = anchor
    ? `${MONTH_NAMES[anchor.month - 1]} ${anchor.year}`
    : '';

  const pending = [];
  const ds = report?.data_status;
  if (ds) {
    if (!ds.salary?.available) pending.push('Payroll not closed');
    if (!ds.levy?.available) pending.push('Levy bill pending');
    if (!ds.dorm?.available) pending.push('Dorm data pending');
  }

  const kpis = [
    {
      title: 'Total Labour Cost',
      titleShort: 'Total Cost',
      value: fmtMoney(totalCost),
      sub: periodLabel,
      icon: Wallet,
      bg: 'bg-blue-100',
      fg: 'text-blue-700',
      delta: momPct,
      deltaLabel: priorLabel,
    },
    {
      title: 'Salary',
      value: fmtMoney(salaryCost),
      sub: `${sharePct(salaryCost, totalCost)}% of total`,
      icon: Banknote,
      bg: 'bg-emerald-100',
      fg: 'text-emerald-600',
    },
    {
      title: 'Foreign Worker Levy',
      titleShort: 'FW Levy',
      value: fmtMoney(levyCost),
      sub: `${sharePct(levyCost, totalCost)}% of total`,
      icon: Landmark,
      bg: 'bg-amber-100',
      fg: 'text-amber-600',
    },
    {
      title: 'Dormitory',
      value: fmtMoney(dormCost),
      sub: `${sharePct(dormCost, totalCost)}% of total`,
      icon: BedDouble,
      bg: 'bg-purple-100',
      fg: 'text-purple-600',
    },
  ];

  const otShare = sharePct(otCost, totalCost);

  return (
    <section className="space-y-6" aria-label="Project Labour Cost">
      <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
        <div className="flex items-start justify-between gap-3 mb-4 sm:mb-6">
          <div className="space-y-1.5 sm:space-y-2 min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <Wallet className="w-5 h-5 sm:w-6 sm:h-6 text-blue-700 flex-shrink-0" />
              <h2 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">Project Labour Cost</h2>
            </div>
            <p className="text-xs sm:text-sm text-gray-600">
              {periodLabel
                ? `Labour cost incurred for ${periodLabel} — salary, levy and dormitory, by project`
                : 'Salary, levy and dormitory cost incurred per project'}
            </p>
          </div>
          <Link
            to={FULL_REPORT}
            className="hidden sm:inline-flex items-center gap-1 text-sm font-medium text-blue-700 hover:text-blue-800 flex-shrink-0"
          >
            Open full report <ChevronRight className="w-4 h-4" />
          </Link>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6 animate-pulse">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
                  <div className="w-9 h-9 sm:w-12 sm:h-12 bg-gray-200 rounded-full flex-shrink-0" />
                  <div className="min-w-0 flex-1 space-y-2">
                    <div className="h-6 w-20 bg-gray-200 rounded" />
                    <div className="h-4 w-24 bg-gray-200 rounded" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : !report || totalCost <= 0 ? (
          <div className="py-10 text-center">
            <BarChart3 className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm font-medium text-gray-500">No project labour cost data available yet.</p>
            <p className="text-xs text-gray-400 mt-1">Costs appear once a payroll run is closed for the period.</p>
          </div>
        ) : (
          <div className="space-y-5 sm:space-y-6">
            {/* KPI cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
              {kpis.map((kpi) => {
                const hasDelta = kpi.delta !== null && kpi.delta !== undefined;
                const up = hasDelta && kpi.delta > 0;
                const flat = hasDelta && Math.abs(kpi.delta) < 0.05;
                return (
                  <div key={kpi.title} className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
                      <div className={`w-9 h-9 sm:w-12 sm:h-12 ${kpi.bg} rounded-full flex items-center justify-center flex-shrink-0`}>
                        <kpi.icon className={`w-4 h-4 sm:w-6 sm:h-6 ${kpi.fg}`} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-lg sm:text-2xl font-bold text-gray-900 tabular-nums">{kpi.value}</p>
                        <p className="text-xs sm:text-sm text-gray-600 leading-snug">
                          {kpi.titleShort ? (
                            <>
                              <span className="sm:hidden">{kpi.titleShort}</span>
                              <span className="hidden sm:inline">{kpi.title}</span>
                            </>
                          ) : (
                            kpi.title
                          )}
                        </p>
                        {kpi.sub && (
                          <p className="text-[11px] sm:text-xs text-gray-500 mt-1 leading-snug">{kpi.sub}</p>
                        )}
                        {hasDelta && (
                          <div
                            className={`mt-1.5 inline-flex max-w-full flex-wrap items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] sm:text-[11px] font-medium ${
                              flat
                                ? 'bg-gray-100 text-gray-600'
                                : up
                                  ? 'bg-red-100 text-red-700'
                                  : 'bg-green-100 text-green-700'
                            }`}
                            title={`Total labour cost ${up ? 'up' : flat ? 'flat' : 'down'} ${Math.abs(kpi.delta).toFixed(1)}% vs ${kpi.deltaLabel}`}
                          >
                            {!flat && (up ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />)}
                            {`${up ? '+' : ''}${kpi.delta.toFixed(1)}% vs ${kpi.deltaLabel}`}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Incomplete-data honesty banner */}
            {pending.length > 0 && (
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5">
                <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-800">
                  Partial figures — {pending.join(' · ')}. Total will rise once all sources are in.
                </p>
              </div>
            )}

            {/* Overtime — a slice of total cost, the most controllable lever */}
            {otCost > 0 && (
              <div className="flex items-center gap-3 rounded-xl bg-gray-50 border border-gray-200 px-3 sm:px-4 py-3">
                <div className="w-9 h-9 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Clock className="w-4 h-4 text-orange-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 tabular-nums">
                    {fmtMoney(otCost)} <span className="font-normal text-gray-500">in overtime</span>
                  </p>
                  <p className="text-[11px] text-gray-500 leading-snug">
                    {otShare}% of labour cost · the most controllable cost lever
                  </p>
                </div>
                <div className="ml-auto hidden sm:block w-28 h-1.5 bg-gray-200 rounded-full overflow-hidden flex-shrink-0">
                  <div
                    className="h-full bg-orange-500 rounded-full"
                    style={{ width: `${Math.min(100, otShare)}%` }}
                  />
                </div>
              </div>
            )}

            {/* Top projects by labour cost */}
            {topProjects.length > 0 && (
              <div className="rounded-xl bg-gray-50 border border-gray-200 p-4 sm:p-5">
                <div className="flex items-center justify-between mb-3 sm:mb-4">
                  <h3 className="text-sm font-semibold text-gray-700">Top projects by labour cost</h3>
                  <Link
                    to={FULL_REPORT}
                    className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:text-blue-800"
                  >
                    <span className="hidden sm:inline">All projects</span>
                    <span className="sm:hidden">All</span>
                    <ChevronRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
                <ul className="space-y-3 sm:space-y-3.5">
                  {topProjects.map((p, i) => (
                    <li key={p.key} className="flex items-start gap-3">
                      <span className="w-4 text-xs font-semibold text-gray-400 tabular-nums flex-shrink-0 text-right">{i + 1}</span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline justify-between gap-3 mb-1">
                          <div className="min-w-0 flex-1">
                            <div className="text-xs sm:text-sm text-gray-700 leading-snug">{p.name}</div>
                            {p.code && p.code !== p.name && (
                              <div className="text-[11px] text-gray-400 font-mono leading-tight">{p.code}</div>
                            )}
                          </div>
                          <span className="text-xs sm:text-sm font-semibold text-gray-900 tabular-nums flex-shrink-0">
                            {fmtMoney(p.total)}
                            <span className="ml-1.5 font-normal text-gray-400">{sharePct(p.total, totalCost)}%</span>
                          </span>
                        </div>
                        <div className="h-1.5 w-full bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-blue-500 rounded-full"
                            style={{ width: `${topMax > 0 ? Math.max(2, (p.total / topMax) * 100) : 0}%` }}
                          />
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default ProjectLabourCostSection;
